/**
 * CommunityUtils
 *
 * Pure helpers for the community wall. Filed here (not inline in a use case) per
 * conventions.md § "New responsibility -> new owner file": the reply-preview
 * construction is a self-contained pure function reused by the add-message use
 * case (and available to any presentation-side preview render).
 */

import type { CommunityChatMessageEntity } from '@/domain/entities/community/communityChatMessageEntity';
import type { CommunityReplyEntity } from '@/domain/entities/community/communityReplyEntity';
import { CommunityChatMessageType } from '@/domain/enums/community/communityChatMessageType';
import { InputLengthFormatters } from '@/domain/constants/inputLengthFormatters';

/**
 * Whether a community message `type` is a media (image or video) message — the
 * predicate Flutter uses to branch into the media-reply build
 * (community_screen.dart:422-423 / :501-502) and the media-render branch
 * (community_message_item.dart:352-384). Video is included for parity even
 * though web video send/render is deferred (task-prompt line 27): a media reply
 * TARGET may still be a Flutter-authored video message, and Flutter's reply
 * build covers both image and video.
 */
export function isCommunityMediaMessageType(type: CommunityChatMessageType): boolean {
  return (
    type === CommunityChatMessageType.image || type === CommunityChatMessageType.video
  );
}

/**
 * Build the `CommunityReplyEntity` snapshot embedded on a new message that
 * replies to `repliedMessage`. Handles both a text reply target and a media
 * (image/video) reply target — the media-reply DATA is now ported (the reply
 * preview *rendering* stays deferred under the marker at
 * CommunityMessageReplyPreview.tsx:85).
 *
 * Base fields — parity with `community_screen.dart:493-499` (`addMessage`) /
 * `:415-420` (`_addMediaMessage`):
 *   - `previewText` is the replied message's full `text` when
 *     `text.length < communityReplyPreviewMaxLength` (strict `<`,
 *     community_screen.dart:482-487), else the first
 *     `communityReplyPreviewMaxLength` chars with a trailing `"..."`
 *     (community_screen.dart:488-491). Because the predicate is strict `<`, a
 *     text of EXACTLY 80 chars takes the else branch: it is not CUT (the
 *     substring keeps all 80 chars) but a trailing `"..."` is still appended —
 *     only a text strictly LONGER than 80 has characters removed.
 *   - `userName` / `userId` come from the replied message's
 *     `messageUsersInfo` (the replied author, not the wall owner) —
 *     community_screen.dart:495 / :497.
 *   - `replyId` is the replied message's id (community_screen.dart:498).
 *   - `type` is the replied message's `type` (community_screen.dart:499).
 *     Flutter quirk fixed in both clients: `_addMediaMessage`'s base OMITTED
 *     `type` (:415-420) and set it only inside the media branch (:430), while
 *     `addMessage` always sets it on the base (:499). `type` is a `late int`
 *     (community_reply.dart:31), so on Flutter the omission was NOT harmless —
 *     it threw a LateInitializationError at doc-write serialization for a media
 *     send replying to a TEXT reply target (the generated `toJson` reads
 *     `instance.type` unconditionally), so nothing persisted at all. Flutter's
 *     reader also requires the key (`(json['type'] as num).toInt()`), making an
 *     always-set `type` load-bearing for cross-client reads. This single shared
 *     builder always sets `type`; the Flutter base builder now does too.
 *
 * Media-reply branch — community_screen.dart:501-511 (`addMessage`) /
 * :422-431 (`_addMediaMessage`): when the replied message is image/video,
 * additionally carry `aspectRatio`, `relativePath`, `relativeThumbnailPath` from
 * the reply target's `mediaInfo`. The absolute `path` / `thumbnail` are the web
 * equivalent of Flutter's `get path` / `get thumbnail` bucket-prefix getters
 * (community_reply.dart:17-29): copied from the source entity's already-derived
 * absolute fields (only the relatives persist via `CommunityReplyRemapper`).
 */
export function buildCommunityReplyPreview(
  repliedMessage: CommunityChatMessageEntity,
): CommunityReplyEntity {
  const text = repliedMessage.text;
  const maxLength = InputLengthFormatters.communityReplyPreviewMaxLength;

  // Strict `<` — parity with community_screen.dart:482-491. Under 80 chars: the
  // full text, no "...". At/over 80: `substring(0, 80) + "..."`. At exactly 80
  // the substring keeps all 80 chars but "..." is still appended; only over 80
  // are characters actually cut.
  const previewText =
    text.length < maxLength ? text : `${text.substring(0, maxLength)}...`;

  const reply: CommunityReplyEntity = {
    replyId: repliedMessage.id,
    userName: repliedMessage.messageUsersInfo.userName,
    userId: repliedMessage.messageUsersInfo.userId,
    previewText,
    type: repliedMessage.type,
  };

  // Media-reply branch (community_screen.dart:501-511 / :422-431). `aspectRatio`
  // is set ONLY here (not on the base) — parity with Flutter setting it only in
  // the media branch (:426 / :505). `mediaInfo` is null-asserted in Flutter
  // (:505-508) so on a well-formed media message it is always present; the web
  // reads it defensively (`?.`) and leaves each field undefined if absent.
  if (isCommunityMediaMessageType(repliedMessage.type)) {
    const media = repliedMessage.mediaInfo;
    reply.aspectRatio = media?.aspectRatio;
    reply.relativePath = media?.relativePath;
    reply.relativeThumbnailPath = media?.relativeThumbnailPath;
    // Web equivalent of Flutter's `get path` / `get thumbnail` derivation: copy
    // the source entity's already-derived absolute URLs (only the relatives
    // persist via CommunityReplyRemapper.toDtoFromEntity).
    reply.path = media?.path;
    reply.thumbnail = media?.thumbnail;
  }

  return reply;
}

/**
 * Comparator that sorts community-wall messages by `createdTimestamp` in
 * descending order (newest first / lower index = newer). Mirrors how the chat
 * port keeps its reversed list sorted via `ChatUtils.sortByTimestampDesc`
 * (chatUtils.ts:213-218), and the Flutter community wall renders newest-first
 * (`community_screen.dart` reads `_messages` index 0 as the newest, inserting
 * new arrivals at index 0 — community_screen.dart:313).
 */
export function sortCommunityMessagesByCreatedDesc(
  a: CommunityChatMessageEntity,
  b: CommunityChatMessageEntity,
): number {
  return b.createdTimestamp - a.createdTimestamp;
}
